import { accessAccountResource, accountResourceTarget } from './accountResourcesClient.js';
import { createConflictClient } from './conflictClient.js';
import { createDirectoryClient } from './directoryClient.js';
import { getPendingInvitation, clearPendingInvitation } from './invitationContext.js';
import ModalDialog from './components/ModalDialog.jsx';
import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';

// === TOAST NOTIFICATION SYSTEM ===
const ToastContext = createContext(null);

// === CRISIS MODAL CONTEXT ===
// Allows any deeply nested component to trigger the crisis resource modal
const CrisisContext = createContext(null);

function useCrisisAlert() {
  return useContext(CrisisContext);
}

function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback to browser alert if context is not available
    return (msg) => window.alert(msg);
  }
  return ctx;
}

function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      {/* Toast container, fixed at bottom center. Always mounted so screen readers announce new toasts. */}
      <div role="status" aria-live="polite" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-2 items-center pointer-events-none" style={{ maxWidth: 'calc(100vw - 2rem)' }}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            onClick={() => dismissToast(toast.id)}
            className="pointer-events-auto bg-stone-800 text-white px-5 py-3 rounded-xl shadow-lg text-sm max-w-md text-center cursor-pointer animate-[toastIn_0.3s_ease-out]"
            style={{ wordBreak: 'break-word' }}
          >
            {toast.message}
          </div>
        ))}
      </div>
      <style>{`
        @keyframes toastIn {
          0% { opacity: 0; transform: translateY(16px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

// === AUDIT TRAIL UTILITY ===
// Calculate working hours between two dates, respecting responseHours setting
function calculateWorkingHours(startDate, endDate, responseHours) {
  const created = new Date(startDate);
  const resolved = new Date(endDate);
  
  let startHour, endHour;
  if (!responseHours || responseHours === '24') {
    startHour = 0;
    endHour = 24;
  } else if (responseHours === '9-5') {
    startHour = 9;
    endHour = 17;
  } else if (responseHours === '5-9') {
    startHour = 17;
    endHour = 21;
  } else {
    const parts = responseHours.split('-').map(h => parseInt(h));
    startHour = parts[0] || 0;
    endHour = parts[1] || 24;
  }
  
  let totalMinutes = 0;
  let currentDate = new Date(created);
  
  while (currentDate < resolved) {
    const hour = currentDate.getHours();
    
    let isInWindow = false;
    if (startHour === 0 && endHour === 24) {
      isInWindow = true;
    } else if (startHour < endHour) {
      isInWindow = hour >= startHour && hour < endHour;
    } else {
      isInWindow = hour >= startHour || hour < endHour;
    }
    
    if (isInWindow) {
      const nextHour = new Date(currentDate);
      nextHour.setHours(currentDate.getHours() + 1, 0, 0, 0);
      const endOfThisSegment = nextHour > resolved ? resolved : nextHour;
      const minutesInThisHour = (endOfThisSegment - currentDate) / (1000 * 60);
      totalMinutes += minutesInThisHour;
    }
    
    currentDate.setHours(currentDate.getHours() + 1, 0, 0, 0);
    if (currentDate - created > 365 * 24 * 60 * 60 * 1000) break;
  }
  
  return totalMinutes / 60;
}

// Check if the current time is within the conflict's agreed engagement hours.
// Returns { allowed, currentHour, startHour, endHour, minutesUntilOpen }
// NOTE: CreateConflictView stores preset shorthands ('9-5' = 9AM-5PM, '5-9' = 5PM-9PM)
// while custom hours use 24h format (e.g., '9-17'). We must map presets first.
function isWithinResponseHours(conflict) {
  const responseHours = conflict?.responseHours;
  
  // If no hours set or '24', always allowed
  if (!responseHours || responseHours === '24') {
    return { allowed: true, currentHour: new Date().getHours(), startHour: 0, endHour: 24, minutesUntilOpen: 0 };
  }
  
  // Map preset shorthands to 24-hour values
  let startHour, endHour;
  if (responseHours === '9-5') {
    // 9 AM to 5 PM
    startHour = 9;
    endHour = 17;
  } else if (responseHours === '5-9') {
    // 5 PM to 9 PM
    startHour = 17;
    endHour = 21;
  } else {
    // Custom format already in 24h (e.g., '9-17', '22-6')
    const parts = responseHours.split('-').map(h => parseInt(h));
    startHour = parts[0] || 0;
    endHour = parts[1] || 24;
  }
  
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeInMinutes = currentHour * 60 + currentMinute;
  
  let allowed = false;
  let minutesUntilOpen = 0;
  
  if (startHour === 0 && endHour === 24) {
    allowed = true;
  } else if (startHour < endHour) {
    // Normal range, e.g., 9-17 (9 AM to 5 PM)
    allowed = currentHour >= startHour && currentHour < endHour;
    if (!allowed) {
      const startTimeInMinutes = startHour * 60;
      if (currentTimeInMinutes < startTimeInMinutes) {
        minutesUntilOpen = startTimeInMinutes - currentTimeInMinutes;
      } else {
        // Past end hour — next opening is tomorrow
        minutesUntilOpen = (24 * 60 - currentTimeInMinutes) + startTimeInMinutes;
      }
    }
  } else {
    // Overnight range, e.g., 22-6 (10 PM to 6 AM)
    allowed = currentHour >= startHour || currentHour < endHour;
    if (!allowed) {
      const startTimeInMinutes = startHour * 60;
      minutesUntilOpen = startTimeInMinutes - currentTimeInMinutes;
      if (minutesUntilOpen < 0) minutesUntilOpen += 24 * 60;
    }
  }
  
  return { allowed, currentHour, startHour, endHour, minutesUntilOpen };
}

// ═══════════════════════════════════════════════════════════════════
//  RELATIONSHIP SYSTEM — Universal relationship model
//  All relationships (mentor-participant, fotw-mentor, omni-fotw)
//  use the same record format. Access is derived from the chain.
// ═══════════════════════════════════════════════════════════════════

// Storage key for all relationships
const RELATIONSHIPS_KEY = 'relationships';

// Migrate legacy mentor-connections to universal format
function migrateRelationships(oldConnections) {
  if (!Array.isArray(oldConnections)) return [];
  return oldConnections.map(c => ({
    id: c.id || `rel-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
    type: 'mentor-participant',
    
    initiatorId: c.initiatedBy === c.mentorId ? c.mentorId : c.menteeId,
    initiatorEmail: c.initiatedBy === c.mentorId ? c.mentorEmail : c.menteeEmail,
    initiatorName: c.initiatedBy === c.mentorId ? c.mentorName : c.menteeName,
    initiatorRole: c.initiatedBy === c.mentorId ? 'mentor' : 'participant',
    
    receiverId: c.initiatedBy === c.mentorId ? c.menteeId : c.mentorId,
    receiverEmail: c.initiatedBy === c.mentorId ? c.menteeEmail : c.mentorEmail,
    receiverName: c.initiatedBy === c.mentorId ? c.menteeName : c.mentorName,
    receiverRole: c.initiatedBy === c.mentorId ? 'participant' : 'mentor',
    
    // Preserve the mentor/participant IDs for easy lookup
    mentorId: c.mentorId,
    participantId: c.menteeId,
    
    status: c.status === 'accepted' ? 'active' : c.status, // accepted → active
    invitedAt: c.sentAt || c.createdAt || new Date().toISOString(),
    acceptedAt: c.respondedAt || (c.status === 'accepted' ? c.sentAt : null),
    revokedAt: null,
    revokedBy: null,
    
    createdVia: 'migration',
    organizationId: null,
    
    // Keep legacy fields for backward compat during transition
    _legacyMentorId: c.mentorId,
    _legacyMenteeId: c.menteeId,
  }));
}

// Load all relationships for a user (as either party, any type)
// S2: tenant-scoped. Falls back to PUBLIC_TENANT for users without an org affiliation.
async function loadRelationships(userId, tenantId = 'tenant-public') {
  try {
    const key = getTenantRelsKey(tenantId);
    const rels = await storage.get(key, true);
    if (!Array.isArray(rels)) return [];
    return rels.filter(r =>
      r.initiatorId === userId ||
      r.receiverId === userId ||
      r.mentorId === userId ||
      r.participantId === userId
    );
  } catch (e) {
    console.error('Error loading relationships:', e);
    return [];
  }
}

// Load ALL relationships (for dashboard filtering across multiple conflicts)
// Falls back to PUBLIC_TENANT for users without an org affiliation.
async function loadAllRelationships(tenantId = 'tenant-public') {
  try {
    const key = getTenantRelsKey(tenantId);
    const rels = await storage.get(key, true);
    return Array.isArray(rels) ? rels : [];
  } catch (e) {
    console.error('Error loading all relationships:', e);
    return [];
  }
}

// Save a relationship (add or update)
// S2: tenant-scoped. Stamps tenantId onto the relationship record.
async function saveRelationship(relationship, allRelationships, tenantId = 'tenant-public') {
  try {
    const stamped = { ...relationship, tenantId: relationship.tenantId || tenantId };
    const updated = [...allRelationships.filter(r => r.id !== stamped.id), stamped];
    const key = getTenantRelsKey(tenantId);
    await storage.set(key, updated, true);
    return updated;
  } catch (e) {
    console.error('Error saving relationship:', e);
    throw e;
  }
}

// ── RelationshipsContext — single session-level source of truth ──
const RelationshipsContext = React.createContext({ relationships: [], refreshRelationships: () => {}, loading: true });

function RelationshipsProvider({ user, children }) {
  const [relationships, setRelationships] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const refresh = React.useCallback(async () => {
    if (!user) { setRelationships([]); setLoading(false); return; }
    try {
      const rels = await loadAllRelationships(user.tenantId || 'tenant-public');
      setRelationships(rels);
    } catch (e) {
      console.error('[RelationshipsContext] Error loading relationships:', e);
    }
    setLoading(false);
  }, [user?.id, user?.tenantId]);

  React.useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 10000);
    return () => clearInterval(interval);
  }, [refresh]);

  const value = React.useMemo(() => ({ relationships, refreshRelationships: refresh, loading }), [relationships, refresh, loading]);

  return React.createElement(RelationshipsContext.Provider, { value }, children);
}

function useRelationships() {
  return React.useContext(RelationshipsContext);
}

// Derive what access a user has to a specific conflict
// Returns: { role, canRead, canWrite, canComment, canVote, canSubmit, canReview, accessPath }
function deriveConflictAccess(conflict, user, relationships) {
  if (!conflict || !user) {
    return { role: null, canRead: false, canWrite: false, canComment: false, canVote: false, canSubmit: false, canReview: false, accessPath: null };
  }
  
  const participantIds = (conflict.mentees || []).map(m => m.id);
  const activeRels = (relationships || []).filter(r => r.status === 'active');
  
  // Helper: find mentor IDs for this conflict's participants
  const getMentorIdsForConflict = () => {
    const ids = new Set();
    activeRels.forEach(r => {
      if (r.type === 'mentor-participant') {
        const mentorId = r.mentorId || (r.initiatorRole === 'mentor' ? r.initiatorId : r.receiverId);
        const partId = r.participantId || (r.initiatorRole === 'participant' ? r.initiatorId : r.receiverId);
        if (participantIds.includes(partId)) ids.add(mentorId);
      }
    });
    return ids;
  };
  
  // 1. Direct participant? (observer/global roles NEVER participate — role wins over membership)
  const isObserverRole = ['fly-on-wall', 'fly-on-the-wall', 'omniscient'].includes(user.role);
  const isParticipant = !isObserverRole && (conflict.mentees?.some(
    m => m.id === user.id || m.email?.toLowerCase() === user.email?.toLowerCase()
  ) || conflict.createdBy === user.id);

  if (isParticipant) {
    return {
      role: 'participant',
      canRead: true, canWrite: true, canComment: true,
      canVote: true, canSubmit: true, canReview: false,
      accessPath: 'Direct participant'
    };
  }
  
  // 2. Mentor connected to a participant?
  const mentorIds = getMentorIdsForConflict();
  if (mentorIds.has(user.id)) {
    // Find which participant(s) they mentor
    const mentoredParticipants = activeRels
      .filter(r => {
        if (r.type !== 'mentor-participant') return false;
        const mid = r.mentorId || (r.initiatorRole === 'mentor' ? r.initiatorId : r.receiverId);
        const pid = r.participantId || (r.initiatorRole === 'participant' ? r.initiatorId : r.receiverId);
        return mid === user.id && participantIds.includes(pid);
      })
      .map(r => {
        const pid = r.participantId || (r.initiatorRole === 'participant' ? r.initiatorId : r.receiverId);
        return conflict.mentees?.find(m => m.id === pid)?.name || 'participant';
      });
    
    return {
      role: 'mentor',
      canRead: true, canWrite: true, canComment: true,
      canVote: false, canSubmit: false, canReview: true,
      accessPath: `Mentor of ${mentoredParticipants.join(', ')}`
    };
  }
  
  // 3. Observer (Fly on the Wall) connected to a mentor who has access?
  const fotwMentorId = activeRels.find(r => {
    if (r.type !== 'fotw-mentor') return false;
    const fotwId = r.initiatorRole === 'fly-on-wall' ? r.initiatorId : r.receiverId;
    const mid = r.initiatorRole === 'mentor' ? r.initiatorId : r.receiverId;
    return fotwId === user.id && mentorIds.has(mid);
  });
  
  if (fotwMentorId) {
    const mid = fotwMentorId.initiatorRole === 'mentor' ? fotwMentorId.initiatorId : fotwMentorId.receiverId;
    const mentorName = fotwMentorId.initiatorRole === 'mentor' ? fotwMentorId.initiatorName : fotwMentorId.receiverName;
    return {
      role: 'fly-on-wall',
      canRead: true, canWrite: false, canComment: true,
      canVote: false, canSubmit: false, canReview: false,
      accessPath: `Observer via mentor ${mentorName || mid}`
    };
  }
  
  // 4. Global (Omniscient) connected to an Observer in the chain?
  const fotwIds = new Set();
  activeRels.forEach(r => {
    if (r.type === 'fotw-mentor') {
      const fotwId = r.initiatorRole === 'fly-on-wall' ? r.initiatorId : r.receiverId;
      const mid = r.initiatorRole === 'mentor' ? r.initiatorId : r.receiverId;
      if (mentorIds.has(mid)) fotwIds.add(fotwId);
    }
  });
  
  const omniRel = activeRels.find(r => {
    if (r.type !== 'omni-fotw') return false;
    const omniId = r.initiatorRole === 'omniscient' ? r.initiatorId : r.receiverId;
    const fId = r.initiatorRole === 'fly-on-wall' ? r.initiatorId : r.receiverId;
    return omniId === user.id && fotwIds.has(fId);
  });
  
  if (omniRel) {
    return {
      role: 'omniscient',
      canRead: true, canWrite: false, canComment: false,
      canVote: false, canSubmit: false, canReview: false,
      accessPath: 'Global observer'
    };
  }
  
  // 5. Admin override
  if (user.role === 'admin') {
    return {
      role: 'admin',
      canRead: true, canWrite: true, canComment: true,
      canVote: false, canSubmit: false, canReview: true,
      accessPath: 'Platform administrator'
    };
  }

  // 6. Support override — cross-tenant read-only (platform staff only, never participates)
  if (user.role === 'support') {
    return {
      role: 'support',
      canRead: true, canWrite: false, canComment: false,
      canVote: false, canSubmit: false, canReview: false,
      accessPath: 'Platform support'
    };
  }

  // No access
  return {
    role: null, canRead: false, canWrite: false, canComment: false,
    canVote: false, canSubmit: false, canReview: false, accessPath: null
  };
}

// Compute a snapshot of all users with relational access to a conflict.
// Returns array of { id, name, email, role, accessPath } for each user with canRead access
// via relationship chains (mentor→participant, FOTW→mentor, global→FOTW).
// Does NOT include direct participants, admin, or support — only relationship-derived access.
function computeRelationalAccessSnapshot(conflict, relationships) {
  const participantIds = (conflict.mentees || []).map(m => m.id).filter(Boolean);
  const activeRels = (relationships || []).filter(r => r.status === 'active');
  const snapshot = [];
  const seen = new Set();

  // Find mentor IDs connected to participants
  const mentorMap = new Map(); // mentorId → { name, email, via }
  activeRels.forEach(r => {
    if (r.type !== 'mentor-participant') return;
    const mentorId = r.mentorId || (r.initiatorRole === 'mentor' ? r.initiatorId : r.receiverId);
    const mentorName = r.initiatorRole === 'mentor' ? r.initiatorName : r.receiverName;
    const mentorEmail = r.initiatorRole === 'mentor' ? r.initiatorEmail : r.receiverEmail;
    const partId = r.participantId || (r.initiatorRole === 'participant' ? r.initiatorId : r.receiverId);
    const partName = conflict.mentees?.find(m => m.id === partId)?.name || 'participant';
    if (participantIds.includes(partId) && !seen.has(mentorId)) {
      seen.add(mentorId);
      mentorMap.set(mentorId, { name: mentorName, email: mentorEmail });
      snapshot.push({ id: mentorId, name: mentorName, email: mentorEmail, role: 'mentor', accessPath: `Mentor of ${partName}` });
    }
  });

  // Find FOTW connected to those mentors
  const fotwMap = new Map();
  activeRels.forEach(r => {
    if (r.type !== 'fotw-mentor') return;
    const fotwId = r.initiatorRole === 'fly-on-wall' ? r.initiatorId : r.receiverId;
    const fotwName = r.initiatorRole === 'fly-on-wall' ? r.initiatorName : r.receiverName;
    const fotwEmail = r.initiatorRole === 'fly-on-wall' ? r.initiatorEmail : r.receiverEmail;
    const mid = r.initiatorRole === 'mentor' ? r.initiatorId : r.receiverId;
    const mentorName = mentorMap.get(mid)?.name || 'mentor';
    if (mentorMap.has(mid) && !seen.has(fotwId)) {
      seen.add(fotwId);
      fotwMap.set(fotwId, { name: fotwName, email: fotwEmail });
      snapshot.push({ id: fotwId, name: fotwName, email: fotwEmail, role: 'observer', accessPath: `Observer via mentor ${mentorName}` });
    }
  });

  // Find Omniscient connected to those FOTWs
  activeRels.forEach(r => {
    if (r.type !== 'omni-fotw') return;
    const omniId = r.initiatorRole === 'omniscient' ? r.initiatorId : r.receiverId;
    const omniName = r.initiatorRole === 'omniscient' ? r.initiatorName : r.receiverName;
    const omniEmail = r.initiatorRole === 'omniscient' ? r.initiatorEmail : r.receiverEmail;
    const fId = r.initiatorRole === 'fly-on-wall' ? r.initiatorId : r.receiverId;
    if (fotwMap.has(fId) && !seen.has(omniId)) {
      seen.add(omniId);
      snapshot.push({ id: omniId, name: omniName, email: omniEmail, role: 'global', accessPath: `Global via observer ${fotwMap.get(fId)?.name}` });
    }
  });

  return snapshot;
}

// Filter conflicts to only those a user can see, scoped to the user's tenant
// Support role: cross-tenant read access (platform staff only)
function getVisibleConflicts(allConflicts, user, relationships) {
  return (allConflicts || []).filter(c => {
    if (user?.role === 'support') return true;
    const conflictTenant = c.tenantId || 'tenant-public';
    const userTenant = user?.tenantId || 'tenant-public';
    const retained = (c.retainedParticipantIds || []).includes(user?.id) && (c.mentees || []).some(p => p.id === user?.id);
    if (conflictTenant !== userTenant && !retained) return false;
    return deriveConflictAccess(c, user, relationships).canRead;
  });
}

// Call addAuditEntry(conflictObj, userId, userName, action, details) before storage.set
// to append an immutable audit record. The history tab renders these.
function addAuditEntry(conflict, userId, userName, action, details = null, userEmail = null) {
  if (!conflict.auditLog) conflict.auditLog = [];
  const entry = {
    userId,
    userName,
    action,
    details,
    timestamp: new Date().toISOString()
  };
  if (userEmail) entry.userEmail = userEmail;
  conflict.auditLog.push(entry);
}

// Firebase Configuration
const FIREBASE_CONFIG = {
  apiKey: "AIzaSyCGCqZFMMIRiicol2_QbyHT3pn7xy_ALSo",
  authDomain: "argumentor-ff17a.firebaseapp.com",
  databaseURL: "https://argumentor-ff17a-default-rtdb.firebaseio.com",
  projectId: "argumentor-ff17a",
  storageBucket: "argumentor-ff17a.firebasestorage.app",
  messagingSenderId: "347151341699",
  appId: "1:347151341699:web:ff8c814095b0c7a7ae63b6",
  measurementId: "G-WKW6WBZKYJ"
};
// Argumentor Owl Logo (base64 embedded)
const OWL_LOGO = 'data:image/png;base64,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';


// Firebase initialization
let firebaseInitialized = false;
let firebaseInitPromise = null;

// === FCM VAPID KEY ===
// Get this from Firebase Console → Project Settings → Cloud Messaging → Web Push certificates
// Click "Generate key pair" if you don't have one yet
const FCM_VAPID_KEY = 'BEhGbW97SWPvveoIxnsR8R-33HS1EcKvSZvJSJcO5VUI0mMVaFZiHMj4uT6R9-C8nLB5eow7qj9XKHmHiItscmo';

// === PWA SERVICE WORKER REGISTRATION ===
const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
      
      // Handle updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'activated' && navigator.serviceWorker.controller) {
            }
          });
        }
      });
      
      return registration;
    } catch (error) {
      console.error('[PWA] Service worker registration failed:', error);
    }
  }
  return null;
};

// === NOTIFICATION HELPERS ===
const notificationPermissionGranted = () => {
  return 'Notification' in window && Notification.permission === 'granted';
};

const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    return false;
  }
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  
  const permission = await Notification.requestPermission();
  return permission === 'granted';
};

const sendNotification = (title, options = {}) => {
  if (!notificationPermissionGranted()) return;
  
  // Don't notify if the page is currently focused
  if (document.hasFocus()) return;
  
  try {
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      // Use service worker for persistent notifications
      navigator.serviceWorker.ready.then(registration => {
        registration.showNotification(title, {
          icon: '/icons/icon-192x192.png',
          badge: '/icons/icon-96x96.png',
          vibrate: [100, 50, 100],
          requireInteraction: false,
          ...options
        });
      });
    } else {
      // Fallback to regular Notification API
      new Notification(title, {
        icon: '/icons/icon-192x192.png',
        ...options
      });
    }
  } catch (err) {
    console.error('[Notify] Failed to send notification:', err);
  }
};

// === FIREBASE CLOUD MESSAGING (FCM) ===
// Initializes FCM after Firebase is ready, requests permission, gets token, and stores it per-user

let fcmInitialized = false;

const initFCM = async (userId) => {
  if (fcmInitialized || !userId) return null;
  if (FCM_VAPID_KEY === 'YOUR_VAPID_KEY_HERE') {
    return null;
  }

  try {
    // Wait for Firebase to be ready
    const fbReady = await initFirebase();
    if (!fbReady) return null;

    // Load Firebase Messaging SDK if not already loaded
    if (!window.firebase.messaging) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://www.gstatic.com/firebasejs/9.22.0/firebase-messaging-compat.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    const messaging = window.firebase.messaging();

    // Wait for service worker to be ready
    const swRegistration = await navigator.serviceWorker.ready;

    // Request notification permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return null;
    }

    // Get FCM token
    const token = await messaging.getToken({
      vapidKey: FCM_VAPID_KEY,
      serviceWorkerRegistration: swRegistration
    });

    if (token) {
      // Store token under user's ID in Firebase RTDB
      const tokenKey = token.substring(0, 12); // Use first 12 chars as key to dedupe
      await window.firebaseDb.ref(`fcmTokens/${userId}/${tokenKey}`).set({
        token: token,
        createdAt: new Date().toISOString(),
        userAgent: navigator.userAgent.substring(0, 100)
      });
      fcmInitialized = true;

      // Handle foreground messages (app is open and focused)
      messaging.onMessage((payload) => {
        // Show notification even in foreground (the sendNotification helper already checks document.hasFocus)
        // For foreground, we use the existing notification system
        if (payload.notification) {
          sendNotification(payload.notification.title, {
            body: payload.notification.body,
            tag: payload.data?.tag || 'argumentor-notification',
            data: payload.data || {},
            renotify: true
          });
        }
      });

      return token;
    }
  } catch (error) {
    console.error('[FCM] Initialization error:', error);
  }
  return null;
};

// Remove FCM token on logout
const removeFCMToken = async (userId) => {
  if (!userId || !window.firebaseDb) return;
  try {
    // Get current token to find the right key
    if (window.firebase.messaging) {
      const messaging = window.firebase.messaging();
      const token = await messaging.getToken({ vapidKey: FCM_VAPID_KEY }).catch(() => null);
      if (token) {
        const tokenKey = token.substring(0, 12);
        await window.firebaseDb.ref(`fcmTokens/${userId}/${tokenKey}`).remove();
        await messaging.deleteToken();
      }
    }
  } catch (error) {
    console.error('[FCM] Error removing token:', error);
  }
  fcmInitialized = false;
};

const initFirebase = async () => {
  if (firebaseInitialized) return true;
  if (firebaseInitPromise) return firebaseInitPromise;

  firebaseInitPromise = new Promise((resolve) => {
    // Check if Firebase is already loaded
    if (window.firebase && window.firebase.database) {
      try {
        if (!window.firebaseApp) {
          window.firebaseApp = window.firebase.initializeApp(FIREBASE_CONFIG);
        }
        window.firebaseDb = window.firebase.database();
        if (window.firebase.auth) {
          window.firebaseAuth = window.firebase.auth();
        }
        firebaseInitialized = true;
        resolve(true);
      } catch (error) {
        console.error('Firebase init error:', error);
        resolve(false);
      }
      return;
    }
    
    // Load Firebase via script tags
    const loadScript = (src) => {
      return new Promise((res, rej) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = res;
        script.onerror = rej;
        document.head.appendChild(script);
      });
    };
    
    // Load Firebase app first, then auth + database in parallel (critical path).
    // Analytics loads in the background — never blocks app initialization.
    let analyticsReady = Promise.resolve();
    loadScript('https://www.gstatic.com/firebasejs/9.22.0/firebase-app-compat.js')
      .then(() => {
        // Start analytics load in background — do NOT wait for it.
        // Analytics init is deferred until after initializeApp() below.
        analyticsReady = loadScript('https://www.gstatic.com/firebasejs/9.22.0/firebase-analytics-compat.js')
          .catch(() => { /* analytics is non-essential */ });

        // Wait only for auth + database (critical path)
        return Promise.all([
          loadScript('https://www.gstatic.com/firebasejs/9.22.0/firebase-auth-compat.js'),
          loadScript('https://www.gstatic.com/firebasejs/9.22.0/firebase-database-compat.js'),
        ]);
      })
      .then(() => {
        try {
          window.firebaseApp = window.firebase.initializeApp(FIREBASE_CONFIG);
          window.firebaseDb = window.firebase.database();
          if (window.firebase.auth) {
            window.firebaseAuth = window.firebase.auth();
          }
          firebaseInitialized = true;
          // Now that the app is initialized, wire up analytics if the script loaded
          analyticsReady.then(() => {
            try { window.firebaseAnalytics = window.firebase.analytics(); }
            catch (e) { /* analytics is non-essential */ }
          });
          resolve(true);
        } catch (error) {
          console.error('Firebase init error:', error);
          resolve(false);
        }
      })
      .catch((error) => {
        console.error('Failed to load Firebase scripts:', error);
        resolve(false);
      });
  });
  
  return firebaseInitPromise;
};

// === GOOGLE ANALYTICS EVENT LOGGING ===
// Lightweight wrapper around firebase.analytics().logEvent()
// Safe to call even if analytics hasn't loaded — silently no-ops.
// Usage: logAnalyticsEvent('conflict_created', { method: 'form' })
const logAnalyticsEvent = (eventName, params = {}) => {
  try {
    if (window.firebaseAnalytics) {
      window.firebaseAnalytics.logEvent(eventName, params);
    }
  } catch (e) {
    // Silent fail — analytics should never break the app
  }
};

// Set the user ID for analytics (call after login)
const setAnalyticsUser = (userId) => {
  try {
    if (window.firebaseAnalytics && userId) {
      window.firebaseAnalytics.setUserId(userId);
    }
  } catch (e) { /* silent */ }
};

// Stripe Configuration (publishable key is client-side safe)
const STRIPE_CONFIG = {
  publishableKey: 'pk_live_51TAc4MAn1idnAKDCaS7PtoXq1TAxLxDsxWPNbw1Zwpt860x71WydeewHixS3ZXxPNdy2AhZlJ1iatddycOTHGjCR00uLrGn54e',
  cloudFunctionUrl: 'https://us-central1-argumentor-ff17a.cloudfunctions.net',
};

// Email Service Configuration
// Uses a single generic EmailJS template with variables:
//   {{to_email}}, {{subject}}, {{greeting}}, {{message_body}}, {{cta_url}}, {{cta_label}}
// All message content is controlled in app logic, not in the template.
const EMAIL_CONFIG = {
  enabled: true,
  serviceId: 'service_5tvziid',
  templateId: 'template_6xd4ukj',
  publicKey: 'KAyprVzfJYyG7m017',
};

// Email Service Functions
const emailService = {
  // Initialize EmailJS (call once on app load)
  init: () => {
    if (EMAIL_CONFIG.enabled && window.emailjs) {
      window.emailjs.init(EMAIL_CONFIG.publicKey);
    }
  },

  // Ensure EmailJS SDK is loaded
  _ensureLoaded: async () => {
    if (window.emailjs) return;
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js';
      script.onload = () => {
        window.emailjs.init(EMAIL_CONFIG.publicKey);
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  },

  // Generic send — all other methods delegate here
  send: async ({ toEmail, subject, greeting, messageBody, ctaUrl, ctaLabel }) => {
    if (!EMAIL_CONFIG.enabled) {
      return { success: true, simulated: true };
    }

    try {
      await emailService._ensureLoaded();

      const response = await window.emailjs.send(
        EMAIL_CONFIG.serviceId,
        EMAIL_CONFIG.templateId,
        {
          to_email: toEmail,
          subject: subject,
          greeting: greeting || 'Hi there,',
          message_body: messageBody,
          cta_url: ctaUrl || window.location.origin,
          cta_label: ctaLabel || 'Open The ArguMentor',
        }
      );

      return { success: true, response };
    } catch (error) {
      console.error('Failed to send email:', error);
      return { success: false, error };
    }
  },

  // Send conflict invitation email
  sendInvitation: async ({ toEmail, inviterName, inviterEmail, conflictTitle, conflictId }) => {
    const baseUrl = window.location.origin;
    const inviteLink = `${baseUrl}?invite=${conflictId}&email=${encodeURIComponent(toEmail)}`;

    return emailService.send({
      toEmail,
      subject: "You've been invited to resolve a conflict on The ArguMentor",
      greeting: 'Hi there,',
      messageBody: `${inviterName} has invited you to participate in a conflict resolution on The ArguMentor, a tool which helps people resolve conflict through structured communication.\n\nConflict: ${conflictTitle}`,
      ctaUrl: inviteLink,
      ctaLabel: 'Join Now',
    });
  },

  // Send mentor connection invite email
  sendMentorConnectionInvite: async ({ toEmail, toName, mentorName, mentorEmail }) => {
    return emailService.send({
      toEmail,
      subject: "You've been invited to connect on The ArguMentor",
      greeting: toName ? `Hi ${toName},` : 'Hi there,',
      messageBody: `${mentorName} has invited you to connect on The ArguMentor, a conflict resolution tool which helps people resolve conflict through structured communication.`,
      ctaUrl: window.location.origin,
      ctaLabel: 'Join Now',
    });
  },

  // Send verification code email
  sendVerificationCode: async (toEmail, code) => {
    return emailService.send({
      toEmail,
      subject: 'Your ArguMentor Verification Code',
      greeting: 'Hi there,',
      messageBody: `Your verification code is: ${code}\n\nThis code expires in 10 minutes. If you did not request this, you can safely ignore this email.`,
      ctaUrl: window.location.origin,
      ctaLabel: 'Open The ArguMentor',
    });
  },
};

// URL Parameter Helpers
const getUrlParams = () => {
  const invitation = getPendingInvitation();
  return { inviteConflictId: invitation?.conflictId, inviteEmail: invitation?.email };
};

const clearUrlParams = () => clearPendingInvitation();

// Voice Input Hook - uses native Web Speech API
const useVoiceInput = (onResult) => {
  const toast = useToast();
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    setIsSupported('webkitSpeechRecognition' in window || 'SpeechRecognition' in window);
  }, []);

  const startListening = useCallback(() => {
    if (!isSupported) {
      toast('Voice input is not supported in this browser. Try Chrome or Edge.');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      onResult(transcript);
      setIsListening(false);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed') {
        toast('Microphone access denied. Please enable microphone permissions.');
      }
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  }, [isSupported, onResult]);

  const stopListening = useCallback(() => {
    setIsListening(false);
  }, []);

  return { isListening, isSupported, startListening, stopListening };
};

// Voice Input Button Component
const VoiceInputButton = ({ onResult, className = '' }) => {
  const { isListening, isSupported, startListening } = useVoiceInput(onResult);

  if (!isSupported) return null;

  return (
    <button
      type="button"
      onClick={startListening}
      className={`p-2 rounded-lg transition-all ${
        isListening 
          ? 'bg-red-100 text-red-600 animate-pulse' 
          : 'bg-stone-100 text-stone-600 hover:bg-amber-100 hover:text-amber-700'
      } ${className}`}
      title={isListening ? 'Listening...' : 'Click to speak'}
    >
      {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
    </button>
  );
};

// === HARM DETECTION & CRISIS SUPPORT SYSTEM ===
// Detects potential self-harm, suicidal ideation, or threats of harm to others
// in user-submitted text and triggers a crisis resource modal + mentor notification.

const HARM_PATTERNS = {
  selfHarm: [
    // Suicidal ideation
    /\b(kill\s+(my|him|her|them)?self|end\s+(my|it\s+all|this)\s*(life)?|sui[cs]id[ea]?[ls]?|tak(?:e|ing|en)\s+my\s+(own\s+)?life(?!\s+back)|don'?t\s+want\s+to\s+(live|be\s+alive|exist)|want\s+to\s+die|rather\s+(be\s+)?dead|better\s+off\s+dead|no\s+reason\s+to\s+live|life\s+is(n'?t)?\s+worth|not\s+worth\s+living|can'?t\s+(go\s+on|take\s+(it|this)\s+(any\s*more)?)|end\s+it\s+all)\b/i, // Added tak(?:e|ing|en) for gerund/past coverage
    // Self-harm
    /\b(cut(?:ting|s)?\s+(my|him|her|them)sel[fv](?!\s+(some|a)\s+(slack|break|deal|favor))|hurt\s+(my|him|her|them)sel[fv]|harm\s+(my|him|her|them)sel[fv]|self[- ]?harm|self[- ]?injur[ey]|burn(?:ing)?\s+(my|him|her|them)sel[fv]|slit(?:ting)?\s+my\s+wrists?|hang(?:ing)?\s+(my|him|her|them)sel[fv](?!\s+up)|hung\s+(my|him|her|them)sel[fv](?!\s+up)|shoot(?:ing)?\s+(my|him|her|them)sel[fv]|shot\s+(my|him|her|them)sel[fv]|strangle\s+(my|him|her|them)sel[fv]|strangling\s+(my|him|her|them)sel[fv]|starv(?:e|ing)\s+(my|him|her|them)sel[fv]|overdos[ei]|swallow\s+(pills|bleach|poison))\b/i, // Added gerunds: cutting, slitting, hanging, shooting, strangling, starving
    // Hopelessness indicators combined with finality
    /\b(no\s+(way\s+out|hope\s+left|one\s+(cares|would\s+miss))|goodbye\s+(cruel\s+)?world|this\s+is\s+my\s+(last|final)|when\s+i'?m\s+gone\s+(no\s+one|they'?ll))\b/i,
  ],
  harmToOthers: [
    // Direct threats
    /\b(i'?m?\s+(going\s+to|gonna|will)\s+(kill|hurt|stab|shoot|attack|beat\s+up|strangle|poison)\s+(you|him|her|them|that|this|my|the))\b/i,
    // Planning violence
    /\b(plan(ning)?\s+to\s+(hurt|attack|kill|harm)|bring(?:ing)?\s+a\s+(gun|knife|weapon)|brought\s+a\s+(gun|knife|weapon)|make\s+(them|him|her|you)\s+(pay|suffer|bleed)|they\s+deserve\s+to\s+die|watch\s+(them|him|her)\s+burn)\b/i, // Added bringing/brought
    // Domestic violence / abuse indicators
    /\b(i'?ll?\s+(beat|hit|smack|punch|slap)\s+(you|him|her|them|my\s+(wife|husband|partner|kid|child)))\b/i,
  ]
};

/**
 * Scans text for unconstructive, dismissive, hostile, or nasty language that
 * undermines the conflict resolution process. This is separate from crisis-level
 * harm detection — this catches tone problems, not safety emergencies.
 * 
 * Returns { isUnconstructive: boolean, reason: string|null }
 */
const UNCONSTRUCTIVE_PATTERNS = [
  // Dismissive language about the other person
  { pattern: /\b(she|he|they)\s+(is|are)\s+(being\s+)?(\w+\s+)?(ridiculous|stupid|dumb|idiotic|pathetic|insane|crazy|delusional|absurd|moronic|brainless|clueless)\b/i, reason: 'Labeling the other person is dismissive. Try expressing how the situation makes you feel instead.' }, // Added optional adverb slot
  { pattern: /\b(you('re| are)\s+(being\s+)?(ridiculous|stupid|dumb|idiotic|pathetic|insane|crazy|delusional|absurd|moronic|brainless|clueless))\b/i, reason: 'Personal labels shut down communication. Try describing the behavior that concerns you instead.' },
  // Name-calling and insults
  { pattern: /\b(idiot|moron|loser|jerk|b[i!]tch|a[s$]{2}hole|dumbass|jackass|scumbag|piece\s+of\s+(shit|crap)|trash|worthless|useless\s+(person|human))\b/i, reason: 'Name-calling is hurtful and unproductive. Focus on the issue, not the person.' },
  // Blanket dismissals
  { pattern: /\b(i\s+shouldn'?t\s+have\s+to|this\s+is\s+(stupid|dumb|ridiculous|pointless|a\s+waste)|don'?t\s+care\s+what\s+(you|they|he|she)\s+think|your\s+(opinion|feelings?)\s+(don'?t|doesn'?t)\s+matter)\b/i, reason: 'Dismissing the process or the other person\'s perspective blocks resolution. Try staying open to their view.' },
  // Contempt / superiority
  { pattern: /\b(grow\s+up|act\s+(like\s+)?an?\s+adult|stop\s+being\s+(a\s+)?(baby|child|drama\s+queen)|get\s+over\s+(it|yourself)|you('re|\s+are)\s+(\w+\s+)?overreacting|so\s+dramatic|always\s+playing\s+the\s+victim)\b/i, reason: 'Telling someone to "grow up" or "get over it" dismisses their feelings. Try acknowledging their experience first.' },
  // Rage / yelling characterizations
  { pattern: /\b(all\s+(she|he|they)\s+do(es)?\s+is\s+(rage|yell|scream|shout|rant|bitch|whine|complain))\b/i, reason: 'Characterizing someone as always yelling or raging is dismissive. Try describing the specific interaction that bothered you.' },
  // Hostile ultimatums
  { pattern: /\b(my\s+way\s+or\s+(the\s+)?highway|take\s+it\s+or\s+leave\s+it|deal\s+with\s+it|tough\s+(luck|shit)|too\s+bad|not\s+my\s+problem|i\s+don'?t\s+give\s+a\s+(damn|shit|crap|fuck))\b/i, reason: 'Ultimatums and hostility prevent finding common ground. Try expressing what you need instead.' },
  // Blame-heavy absolutes
  { pattern: /\b(you\s+always|you\s+never|it'?s\s+(all\s+)?your\s+fault|everything\s+is\s+(always\s+)?about\s+you|you\s+ruin(ed)?\s+everything)\b/i, reason: '"Always" and "never" statements feel like attacks. Try describing specific situations instead.' },
  // Profanity used aggressively (not just casually)
  { pattern: /\b(fuck\s+(you|off|this)|shut\s+(the\s+fuck\s+)?up|go\s+to\s+hell|screw\s+you|kiss\s+my\s+ass|eat\s+shit)\b/i, reason: 'Aggressive language makes the other person feel unsafe. Take a breath and try again.' },
  // Mocking
  { pattern: /\b(oh\s+(boo\s+hoo|poor\s+(you|baby|thing))|cry\s+me\s+a\s+river|what(ever|evs)|lol\s+ok|sure\s+jan|yeah\s+right|as\s+if)\b/i, reason: 'Mocking undermines trust. Try engaging with what they actually said.' },
  // Tier 1 — Standalone hostile words (blocked regardless of phrasing)
  { pattern: /\b(idiotic|moronic|pathetic|disgusting|revolting|brainless|braindead|dimwit|halfwit|nitwit|imbecile|deranged|unhinged|lowlife|deadbeat|good-for-nothing|slob|creep)\b/i, reason: 'This language is hostile and will shut down communication. Try describing the specific behavior that concerns you.' },
];

// === SHARED PROFANITY CHECK ===
const PROFANITY_LIST = [
  'fuck', 'fucking', 'fucked', 'fucker', 'shit', 'shitty', 'damn', 'bitch', 'bitchy',
  'asshole', 'bastard', 'crap', 'piss', 'pissed', 'dick', 'cock', 'pussy', 'slut',
  'whore', 'fag', 'nigger', 'cunt', 'twat', 'wank', 'wanker', 'bloody', 'bugger',
  'arse', 'bollocks', 'prick', 'motherfucker', 'motherfucking',
  'bullshit', 'horseshit', 'dumbass', 'jackass', 'dipshit'
];

const containsProfanity = (text) => {
  if (!text) return false;
  const lowerText = text.toLowerCase();
  return PROFANITY_LIST.some(word => {
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    return regex.test(lowerText);
  });
};

const detectUnconstructiveContent = (text) => {
  if (!text || typeof text !== 'string' || text.trim().length < 5) {
    return { isUnconstructive: false, reason: null };
  }

  const cleaned = text.replace(/[*_~`#]/g, '').trim();

  for (const { pattern, reason } of UNCONSTRUCTIVE_PATTERNS) {
    if (pattern.test(cleaned)) {
      return { isUnconstructive: true, reason };
    }
  }

  return { isUnconstructive: false, reason: null };
};

/**
 * Detects potentially inflammatory or loaded language and suggests neutral alternatives.
 * Unlike unconstructive detection (which blocks), this provides gentle word-choice suggestions.
 * Returns { hasIssues: boolean, suggestions: [{ found, suggestion, context }] }
 */
const CONNOTATION_PATTERNS = [
  // Substance/behavior-related loaded language
  { pattern: /\b(get(?:s|ting)?\s+(?:trashed|wasted|hammered|smashed|plastered|blitzed|bombed|loaded|sloshed))\b/i, suggestion: 'drinking heavily or drinking too much', context: 'This phrasing can sound judgmental. Try describing the behavior directly.' },
  { pattern: /\b(drunk(?:ard)?|alcoholic|lush|boozer|addict)\b/i, suggestion: 'someone who struggles with alcohol or substance use', context: 'Labels can feel like attacks. Try describing the specific behavior that concerns you.' },
  { pattern: /\b(junkie|druggie|pothead|stoner|crackhead)\b/i, suggestion: 'someone who uses substances or has a substance problem', context: 'These terms are stigmatizing. Focus on the behavior that affects you.' },
  // Emotional/mental state loaded language
  { pattern: /\b(throw(?:s|ing|n)?|threw)\s+a\s+(?:fit|tantrum)\b/i, suggestion: 'gets very upset or reacts strongly', context: 'This minimizes their feelings. Try describing what you observed instead.' }, // Added threw (past tense)
  { pattern: /\b(lose(?:s)?\s+(?:their|his|her)\s+(?:mind|shit|marbles))\b/i, suggestion: 'becomes very upset or overwhelmed', context: 'This exaggerates the situation. Try describing the specific reaction.' },
  { pattern: /\b(flip(?:s|ped|ping)?\s+out)\b/i, suggestion: 'reacted strongly or became very upset', context: 'Try describing the specific behavior you observed.' },
  { pattern: /\b(blow(?:s|ing|n)?\s+up)\b/i, suggestion: 'gets very angry or reacts with intense emotion', context: 'Try being specific about what the reaction looks like.' },
  { pattern: /\b(melt(?:s|ing)?\s*down|had\s+a\s+meltdown)\b/i, suggestion: 'became very overwhelmed or distressed', context: 'This can feel dismissive of real distress. Try describing what happened.' },
  { pattern: /\b(go(?:es|ing)?|went)\s+(?:nuts|ballistic|berserk|postal)\b/i, suggestion: 'reacting with extreme frustration or anger', context: 'Exaggerated language can escalate conflict. Try being specific.' }, // Added went/goes (past tense)
  // Controlling/manipulative accusations
  { pattern: /\b(control\s*freak|manipulat(?:ive|or|ing)|gasligh(?:t|ting|ted))\b/i, suggestion: 'tries to control situations or dismisses my perspective', context: 'These are loaded clinical terms. Try describing the specific behaviors that concern you instead.' },
  { pattern: /\b(narcissis(?:t|tic)|sociopath|psycho(?:path)?|toxic)\b/i, suggestion: 'someone whose behavior feels harmful or self-centered', context: 'Diagnostic labels in conflict can feel like attacks. Describe the specific actions.' },
  // Derogatory characterizations
  { pattern: /\b(act(?:s|ing)?\s+like\s+a\s+(?:child|baby|brat|princess|diva|martyr|saint))\b/i, suggestion: 'behaves in a way I find frustrating', context: 'Comparisons like this can feel belittling. Try naming the specific behavior.' },
  { pattern: /\b((?:such|what)\s+a\s+(?:drama|mess|joke|disaster|nightmare|train\s*wreck))\b/i, suggestion: 'a difficult or stressful situation', context: 'Dramatic characterizations can inflame things. Try describing the impact on you.' },
  // Absolutes used as weapons
  { pattern: /\b((?:every(?:one|body))\s+(?:knows|thinks|agrees|says)\s+(?:that\s+)?(?:you|he|she|they))\b/i, suggestion: 'some people have shared concerns about...', context: 'Claiming "everyone" agrees puts the other person on the defensive. Speak for yourself.' },
  { pattern: /\b(nobody\s+(?:likes|wants|cares\s+about)\s+(?:you|your|him|her|them|their))\b/i, suggestion: 'I feel like my concerns aren\'t being heard', context: 'Statements like this are deeply hurtful. Try expressing your own feelings instead.' },
  // Passive-aggressive / loaded phrasing
  { pattern: /\b(if\s+you\s+(?:actually|even|really)\s+cared)\b/i, suggestion: 'I need to feel more care or attention around...', context: 'This questions their motives. Try sharing what you need directly.' },
  { pattern: /\b(must\s+be\s+nice\s+to)\b/i, suggestion: 'I feel frustrated when...', context: 'Sarcasm hides the real issue. Try saying what you actually feel.' },
  { pattern: /\b((?:oh|well)\s+excuse\s+me\s+for)\b/i, suggestion: 'I felt hurt when...', context: 'Sarcasm creates distance. Try being direct about how you feel.' },
  { pattern: /\b(typical(?:ly)?(?:\s+(?:you|him|her|them)))\b/i, suggestion: 'this has happened before and it concerns me', context: 'Calling something "typical" feels dismissive. Try naming the pattern specifically.' },
  // Tier 2 — Standalone loaded words (suggest alternatives, don't block)
  { pattern: /\b(crazy)\b/i, suggestion: 'confusing, overwhelming, or frustrating', context: 'Words like this can feel like a character judgment. Try describing the situation instead.' },
  { pattern: /\b(insane)\b/i, suggestion: 'unbelievable or extremely frustrating', context: 'This can feel dismissive. Try being specific about what bothers you.' },
  { pattern: /\b(lazy)\b/i, suggestion: 'not contributing equally or not following through', context: 'Labels shut down dialogue. Try describing the specific behavior.' },
  { pattern: /\b(selfish)\b/i, suggestion: 'not considering my needs or perspective', context: 'This feels like a character attack. Try naming the specific action.' },
  { pattern: /\b(dramatic)\b/i, suggestion: 'reacting more strongly than I expected', context: 'Calling someone dramatic dismisses their feelings.' },
  { pattern: /\b(obsessed)\b/i, suggestion: 'very focused on or preoccupied with', context: 'This can feel like a judgment. Try describing what you observe.' },
  { pattern: /\b(delusional)\b/i, suggestion: 'seeing the situation very differently than I do', context: 'This is a strong label. Try describing the disagreement specifically.' },
  { pattern: /\b(paranoid)\b/i, suggestion: 'worried or suspicious about', context: 'Clinical-sounding labels can escalate conflict. Describe the concern instead.' },
  { pattern: /\b(controlling)\b/i, suggestion: 'making decisions without my input', context: 'Try describing the specific behavior rather than labeling the person.' },
  { pattern: /\b(needy|clingy)\b/i, suggestion: 'wanting more connection or reassurance than I can give', context: 'These labels dismiss someone\'s needs. Try expressing your own boundaries instead.' },
  { pattern: /\b(childish|immature)\b/i, suggestion: 'behaving in a way I find frustrating or unhelpful', context: 'Age-based labels feel condescending. Try naming the behavior.' },
  { pattern: /\b(ridiculous|absurd)\b/i, suggestion: 'hard to understand or unreasonable to me', context: 'Try explaining why it seems unreasonable rather than labeling it.' },
  { pattern: /\b(heartless|cold)\b/i, suggestion: 'not showing the empathy I need', context: 'Try expressing what you need from them instead of labeling.' },
  { pattern: /\b(ungrateful)\b/i, suggestion: 'not acknowledging or appreciating', context: 'Try describing what you\'d like them to recognize.' },
  { pattern: /\b(nag(?:ging|gy|s)?)\b/i, suggestion: 'repeatedly bringing up concerns', context: 'This word dismisses legitimate concerns. Try understanding why they keep raising it.' },
  { pattern: /\b(passive[- ]aggressive)\b/i, suggestion: 'expressing frustration indirectly', context: 'Try describing the specific indirect behavior you noticed.' },
  { pattern: /\b(stupid)\b/i, suggestion: 'doesn\'t make sense to me or seems like a poor choice', context: 'Try describing why the action or decision concerns you.' },
  { pattern: /\b(dumb)\b/i, suggestion: 'doesn\'t make sense to me', context: 'Try describing what specifically concerns you about it.' },
];

const detectConnotationIssues = (text) => {
  if (!text || typeof text !== 'string' || text.trim().length < 5) {
    return { hasIssues: false, suggestions: [] };
  }

  const cleaned = text.replace(/[*_~`#]/g, '').trim();
  const suggestions = [];

  for (const { pattern, suggestion, context } of CONNOTATION_PATTERNS) {
    const match = cleaned.match(pattern);
    if (match) {
      suggestions.push({
        found: match[0],
        suggestion,
        context
      });
    }
  }

  return { hasIssues: suggestions.length > 0, suggestions };
};
const detectHarmContent = (text) => {
  if (!text || typeof text !== 'string' || text.trim().length < 5) {
    return { detected: false, type: null, matches: [] };
  }

  const cleaned = text.replace(/[*_~`#]/g, '').trim();
  let selfHarmDetected = false;
  let harmToOthersDetected = false;
  const matches = [];

  for (const pattern of HARM_PATTERNS.selfHarm) {
    const match = cleaned.match(pattern);
    if (match) {
      selfHarmDetected = true;
      matches.push(match[0]);
    }
  }

  for (const pattern of HARM_PATTERNS.harmToOthers) {
    const match = cleaned.match(pattern);
    if (match) {
      harmToOthersDetected = true;
      matches.push(match[0]);
    }
  }

  if (!selfHarmDetected && !harmToOthersDetected) {
    return { detected: false, type: null, matches: [] };
  }

  let type = null;
  if (selfHarmDetected && harmToOthersDetected) type = 'both';
  else if (selfHarmDetected) type = 'self';
  else type = 'others';

  return { detected: true, type, matches };
};

/**
 * Send a push notification to a specific user via their FCM tokens stored in Firebase.
 * This is a client-side helper that writes to a "pendingNotifications" queue in RTDB,
 * which the Cloud Function can process — OR — directly sends via local FCM if user is online.
 * For immediate effect, we write to a notification inbox the target user's listener picks up.
 */
const sendPushToUser = async (targetUserId, title, body, data = {}) => {
  if (!window.firebaseDb || !targetUserId) return;
  try {
    await accessAccountResource({ action: 'notify', resource: 'notifications', userId: targetUserId, value: { title, body, data } });
  } catch (e) {
    console.error('[sendPushToUser] Failed to send notification to user:', e);
  }
};

/**
 * Logs a harm detection event to Firebase for safety auditing.
 * Stores minimal info: who, when, what type, which conflict. Text is NOT stored for privacy.
 */
const logHarmDetectionEvent = async (userId, userName, conflictId, harmType, context) => {
  if (!window.firebaseDb) return;
  try {
    const eventId = `harm-${Date.now()}`;
    await window.firebaseDb.ref(`safetyLog/${eventId}`).set({
      userId,
      userName,
      conflictId: conflictId || null,
      harmType,
      context, // e.g. 'paraphrase', 'because-statement', 'alternative', 'mentor-message', etc.
      timestamp: new Date().toISOString(),
      // NOTE: We intentionally do NOT store the actual text for privacy reasons.
    });
  } catch (e) {
    console.error('[HarmDetection] Failed to log safety event:', e);
  }
};

/**
 * Notify mentor about a harm detection event via push notification, in-app notification,
 * and email (if configured).
 */
const notifyMentorOfHarmDetection = async (conflict, user, harmType, context) => {
  if (!conflict) return;
  
  const mentorId = conflict.mentor?.id;
  const mentorEmail = conflict.mentor?.email;
  const mentorName = conflict.mentor?.name;
  
  if (!mentorId) return; // No mentor assigned
  
  const harmLabel = harmType === 'self' ? 'self-harm' 
    : harmType === 'others' ? 'potential harm to others' 
    : 'self-harm or harm to others';
  
  const notifTitle = 'Safety Alert: Concerning Content Detected';
  const notifBody = `${user.name || 'A participant'} in "${conflict.title || 'a conflict'}" may need support. Concerning language (${harmLabel}) was detected in their ${context}. Please check in on them.`;
  
  // 1. In-app push notification via Firebase inbox
  await sendPushToUser(mentorId, notifTitle, notifBody, {
    type: 'HARM_DETECTION',
    conflictId: conflict.id,
    userId: user.id,
    harmType
  });
  
  // 2. Email notification to mentor
  if (EMAIL_CONFIG.enabled && mentorEmail && window.emailjs) {
    try {
      await window.emailjs.send(EMAIL_CONFIG.serviceId, EMAIL_CONFIG.templateId, {
        to_email: mentorEmail,
        inviter_name: 'The ArguMentor Safety System',
        conflict_title: 'Safety Alert: Concerning Content Detected',
        conflict_description: `Dear ${mentorName || 'Mentor'},\n\n` +
          `Our safety system has detected potentially concerning language (${harmLabel}) ` +
          `from ${user.name || 'a participant'} in the conflict "${conflict.title || 'Untitled'}".\n\n` +
          `Context: ${context}\n\n` +
          `Please check in with this participant as soon as possible. ` +
          `If you believe someone is in immediate danger, please call 911 or your local emergency number.\n\n` +
          `The ArguMentor Safety System`,
        app_link: window.location.origin,
        app_name: 'The ArguMentor'
      });
    } catch (e) {
      console.error('[HarmDetection] Failed to send mentor email:', e);
    }
  }
  
  // 3. Log to safety audit trail
  await logHarmDetectionEvent(user.id, user.name, conflict.id, harmType, context);
};

/**
 * Main handler: checks text for harm, shows crisis modal, notifies mentor.
 * Returns true if harm was detected (caller should NOT block submission but should show modal).
 * 
 * Usage in any submit handler:
 *   const harmResult = detectHarmContent(text);
 *   if (harmResult.detected) {
 *     setCrisisModal({ show: true, type: harmResult.type });
 *     notifyMentorOfHarmDetection(conflict, user, harmResult.type, 'context-label');
 *     // Still allow submission — do not block the user
 *   }
 */

// Crisis Resource Modal Component
function CrisisResourceModal({ isOpen, onClose, harmType }) {
  if (!isOpen) return null;
  
  const isSelfHarm = harmType === 'self' || harmType === 'both';
  const isHarmToOthers = harmType === 'others' || harmType === 'both';
  
  return (
    <ModalDialog onClose={onClose} labelledBy="crisis-resource-title" className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-rose-600 rounded-t-2xl px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <Heart className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 id="crisis-resource-title" className="text-xl font-bold text-white">We Care About You</h2>
              <p className="text-white text-sm">You are not alone. Help is available.</p>
            </div>
          </div>
        </div>
        
        <div className="p-6 space-y-5">
          {isSelfHarm && (
            <div className="space-y-4">
              <p className="text-stone-700 leading-relaxed">
                We noticed some language in your message that may indicate you are going through a very difficult time. 
                Your safety matters to us. Please reach out to any of these free, confidential resources:
              </p>
              
              <div className="space-y-3">
                {/* 988 Suicide & Crisis Lifeline */}
                <a href="tel:988" className="flex items-center gap-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl hover:bg-red-100 transition-colors group">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-red-800 text-lg">988 Suicide & Crisis Lifeline</p>
                    <p className="text-red-700 text-sm">Call or text <span className="font-bold">988</span> (24/7, free & confidential)</p>
                  </div>
                </a>
                
                {/* Crisis Text Line */}
                <a href="sms:741741&body=HELLO" className="flex items-center gap-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl hover:bg-blue-100 transition-colors">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-blue-800 text-lg">Crisis Text Line</p>
                    <p className="text-blue-600 text-sm">Text <span className="font-bold">HELLO</span> to <span className="font-bold">741741</span> (24/7)</p>
                  </div>
                </a>
                
                {/* SAMHSA */}
                <a href="tel:1-800-662-4357" className="flex items-center gap-4 p-4 bg-purple-50 border-2 border-purple-200 rounded-xl hover:bg-purple-100 transition-colors">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-purple-800">SAMHSA Helpline</p>
                    <p className="text-purple-600 text-sm">Call <span className="font-bold">1-800-662-4357</span> (24/7, free & confidential)</p>
                  </div>
                </a>

                {/* International Association for Suicide Prevention */}
                <a href="https://www.iasp.info/resources/Crisis_Centres/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 bg-stone-50 border-2 border-stone-200 rounded-xl hover:bg-stone-100 transition-colors">
                  <div className="w-12 h-12 bg-stone-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-stone-800">International Resources</p>
                    <p className="text-stone-600 text-sm">Find crisis centers worldwide (IASP directory)</p>
                  </div>
                </a>
              </div>
            </div>
          )}
          
          {isHarmToOthers && (
            <div className="space-y-4">
              <p className="text-stone-700 leading-relaxed">
                We noticed some language in your message that suggests someone may be at risk of harm. 
                If there is an immediate emergency, please contact your local authorities.
              </p>
              
              <div className="space-y-3">
                {/* 911 */}
                <a href="tel:911" className="flex items-center gap-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl hover:bg-red-100 transition-colors">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-red-800 text-lg">Emergency Services</p>
                    <p className="text-red-700 text-sm">Call <span className="font-bold">911</span> if anyone is in immediate danger</p>
                  </div>
                </a>
                
                {/* National Domestic Violence Hotline */}
                <a href="tel:1-800-799-7233" className="flex items-center gap-4 p-4 bg-purple-50 border-2 border-purple-200 rounded-xl hover:bg-purple-100 transition-colors">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-purple-800">National Domestic Violence Hotline</p>
                    <p className="text-purple-600 text-sm">Call <span className="font-bold">1-800-799-7233</span> (24/7, free & confidential)</p>
                  </div>
                </a>
                
                {/* 988 also relevant */}
                <a href="tel:988" className="flex items-center gap-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl hover:bg-blue-100 transition-colors">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-blue-800">988 Suicide & Crisis Lifeline</p>
                    <p className="text-blue-600 text-sm">Call or text <span className="font-bold">988</span> for any emotional crisis (24/7)</p>
                  </div>
                </a>
              </div>
            </div>
          )}
          
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-800 text-sm font-medium">Your mentor has been notified</p>
                <p className="text-amber-700 text-xs mt-1">
                  If a mentor is assigned to this conflict, they have been alerted so they can offer support. 
                  Your message has still been submitted. We do not block your participation.
                </p>
              </div>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="w-full py-3 bg-stone-800 hover:bg-stone-900 text-white rounded-xl font-medium transition-colors"
          >
            I Understand
          </button>
        </div>
      </div>
    </ModalDialog>
  );
}

// Utility function for persistent storage
// Uses Firebase Realtime Database for shared data across all users/devices
// Falls back to window.storage (Claude) or localStorage
const conflictClient = createConflictClient({ auth: () => window.firebaseAuth,
  baseUrl: 'https://us-central1-argumentor-ff17a.cloudfunctions.net' });

const directoryClient = createDirectoryClient({
  auth: () => window.firebaseAuth,
  endpoint: 'https://us-central1-argumentor-ff17a.cloudfunctions.net/accessDirectory',
});

async function directoryTarget(key) {
  const match = key.match(/^tenant:([^:]+):(users|relationships)$/);
  if (match) return { tenantId: match[1], resource: match[2] };
  if (key === 'relationships' || key.startsWith('user:')) {
    const claims = (await window.firebaseAuth?.currentUser?.getIdTokenResult())?.claims;
    if (!claims?.tenantId) throw new Error('Verified organization required.');
    return { tenantId: claims.tenantId, resource: key === 'relationships' ? 'relationships' : 'users', userId: key.startsWith('user:') ? key.slice(5) : null };
  }
  return null;
}

const storage = {
  subscribeConflicts: callback => conflictClient.subscribe(callback),
  // S7: Convert logical app key → nested Firebase path
  // Logical keys use ':' as separator (e.g. tenant:public:users, conflict:abc123)
  // Firebase paths use '/' for nesting, enabling proper security rules
  toFirebaseKey: (key) => {
    const tenantMatch = key.match(/^tenant:(.+):(users|relationships)$/);
    if (tenantMatch) return 'tenants/' + tenantMatch[1] + '/' + tenantMatch[2];
    if (key.startsWith('conflict:')) return 'conflicts/' + key.slice(9);
    if (key === 'tenants') return 'tenants/_index';
    if (key.startsWith('mood:')) return 'users/' + key.slice(5) + '/mood';
    if (key.startsWith('agreement-signed:')) return 'users/' + key.slice(17) + '/agreement';
    if (key.startsWith('notifications:')) return 'users/' + key.slice(14) + '/notifications';
    if (key.startsWith('mentor-settings:')) return 'users/' + key.slice(16) + '/mentor-settings';
    if (key.startsWith('vote:')) return 'votes/' + key.slice(5);
    if (key.startsWith('siyw-sessions:')) return 'users/' + key.slice(14) + '/siyw-sessions';
    if (key.startsWith('freemium-usage:')) return 'users/' + key.slice(15) + '/freemium-usage';
    if (key.startsWith('stripe-user:')) return 'stripe/users/' + key.slice(12);
    return key.replace(/[.#$[\]]/g, '_');
  },
  fromFirebaseKey: (key) => {
    const tenantMatch = key.match(/^tenants\/(.+)\/(users|relationships)$/);
    if (tenantMatch) return 'tenant:' + tenantMatch[1] + ':' + tenantMatch[2];
    if (key === 'tenants/_index') return 'tenants';
    if (key.startsWith('conflicts/')) return 'conflict:' + key.slice(10);
    const moodMatch = key.match(/^users\/(.+)\/mood$/);
    if (moodMatch) return 'mood:' + moodMatch[1];
    const agreeMatch = key.match(/^users\/(.+)\/agreement$/);
    if (agreeMatch) return 'agreement-signed:' + agreeMatch[1];
    const notifMatch = key.match(/^users\/(.+)\/notifications$/);
    if (notifMatch) return 'notifications:' + notifMatch[1];
    const mentorMatch = key.match(/^users\/(.+)\/mentor-settings$/);
    if (mentorMatch) return 'mentor-settings:' + mentorMatch[1];
    if (key.startsWith('votes/')) return 'vote:' + key.slice(6);
    const siywMatch = key.match(/^users\/(.+)\/siyw-sessions$/);
    if (siywMatch) return 'siyw-sessions:' + siywMatch[1];
    const freemiumMatch = key.match(/^users\/(.+)\/freemium-usage$/);
    if (freemiumMatch) return 'freemium-usage:' + freemiumMatch[1];
    const stripeUserMatch = key.match(/^stripe\/users\/(.+)$/);
    if (stripeUserMatch) return 'stripe-user:' + stripeUserMatch[1];
    return key;
  },
  
  // Generate or retrieve a stable per-device ID so each browser gets its own session in Firebase
  getDeviceId: () => {
    let deviceId = localStorage.getItem('argumentor-device-id');
    if (!deviceId) {
      deviceId = 'device-' + Date.now() + '-' + Math.random().toString(36).substring(2, 9);
      localStorage.setItem('argumentor-device-id', deviceId);
    }
    return deviceId;
  },
  
  // Per-device keys get scoped under a device-specific path in Firebase
  // so that two browsers don't overwrite each other's session
  isSessionKey: (key) => key === 'current-user',
  toSessionFirebaseKey: (key) => {
    const deviceId = storage.getDeviceId();
    return `sessions/${deviceId}/${storage.toFirebaseKey(key)}`;
  },
  
  get: async (key, shared = null) => {
    try {
      const resource = accountResourceTarget(key);
      if (resource) return await accessAccountResource({ ...resource, action: 'read' });
      if (key.startsWith('conflict:')) return await conflictClient.read(key.slice(9));
      const directory = await directoryTarget(key);
      if (directory) {
        const records = await directoryClient.read(directory.tenantId, directory.resource);
        return directory.userId ? records.find(u => u.id === directory.userId) || null : records;
      }
      // Sanitize key for Firebase (replace invalid characters)
      // Session keys use a per-device path so users don't overwrite each other
      const firebaseKey = storage.isSessionKey(key)
        ? storage.toSessionFirebaseKey(key)
        : storage.toFirebaseKey(key);

      // Try Firebase first
      const fbReady = await initFirebase();
      if (fbReady && window.firebaseDb) {
        try {
          const snapshot = await window.firebaseDb.ref(`data/${firebaseKey}`).once('value');
          if (snapshot.exists()) {
            const value = snapshot.val();
            return value;
          }
          return null;
        } catch (fbError) {
          throw fbError; // Never downgrade identity or substitute cached private data.

        }
      }
      
      // Try window.storage (Claude artifact environment)
      if (window.storage && window.storage.get) {
        const isShared = shared !== null ? shared : (key.startsWith('conflict:') || key === 'all-users');
        const result = await window.storage.get(key, isShared);
        if (!result) return null;
        
        let parsed = JSON.parse(result.value);
        
        // Handle double-stringified legacy data
        if (typeof parsed === 'string' && (parsed.startsWith('{') || parsed.startsWith('['))) {
          try {
            parsed = JSON.parse(parsed);
          } catch (e) {}
        }
        
        return parsed;
      }
      
      // Fallback to localStorage
      const item = localStorage.getItem(key);
      if (!item) return null;
      
      let parsed = JSON.parse(item);
      if (typeof parsed === 'string' && (parsed.startsWith('{') || parsed.startsWith('['))) {
        try {
          parsed = JSON.parse(parsed);
        } catch (e) {}
      }
      
      return parsed;
    } catch (error) {
      console.error('Storage get error:', error);
      throw error;
    }
  },
  
  set: async (key, value, shared = null) => {
    try {
      const resource = accountResourceTarget(key);
      if (resource) { await accessAccountResource({ ...resource, action: 'write', value }); return true; }
      if (key.startsWith('conflict:')) return await conflictClient.write(key.slice(9), value);
      const directory = await directoryTarget(key);
      if (directory) {
        if (directory.userId) {
          const records = await directoryClient.read(directory.tenantId, 'users');
          return directoryClient.write(directory.tenantId, 'users', records.map(u => u.id === directory.userId ? value : u));
        }
        return directoryClient.write(directory.tenantId, directory.resource, value);
      }
      // Sanitize key for Firebase
      // Session keys use a per-device path so users don't overwrite each other
      const firebaseKey = storage.isSessionKey(key)
        ? storage.toSessionFirebaseKey(key)
        : storage.toFirebaseKey(key);

      // Firebase is the single source of truth — writes must succeed here
      const fbReady = await initFirebase();
      if (fbReady && window.firebaseDb) {
        try {
          await window.firebaseDb.ref(`data/${firebaseKey}`).set(value);
          return true;
        } catch (fbError) {
          console.error('Firebase set error:', fbError);
          throw fbError; // Surface the error — do not silently fall to localStorage
        }
      }

      // Try window.storage (Claude artifact environment — not production)
      if (window.storage && window.storage.set) {
        const isShared = shared !== null ? shared : (key.startsWith('conflict:') || key === 'all-users');
        await window.storage.set(key, JSON.stringify(value), isShared);
        return true;
      }

      // Firebase not available at all — last resort localStorage
      // This should only happen if Firebase failed to initialize entirely
      console.warn(`[storage.set] Firebase unavailable for key "${key}", falling back to localStorage`);
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error('Storage set error:', error);
      throw error; // Let callers handle the failure
    }
  },
  
  list: async (prefix, shared = null) => {
    try {
      if (prefix === 'conflict:') return (await conflictClient.all()).map(c => `conflict:${c.id}`);
      // Sanitize prefix for Firebase
      const firebasePrefix = storage.toFirebaseKey(prefix);
      
      // Try Firebase first
      const fbReady = await initFirebase();
      if (fbReady && window.firebaseDb) {
        try {
          // S7: nested paths — query the correct nested node directly
          // e.g. 'conflict:' → firebasePrefix = 'conflicts/' → query data/conflicts
          const nestedPath = firebasePrefix.replace(/\/$/, ''); // strip trailing slash
          const snapshot = await window.firebaseDb.ref(`data/${nestedPath}`).once('value');
          if (snapshot.exists()) {
            const allData = snapshot.val();
            if (typeof allData === 'object' && allData !== null) {
              const keys = Object.keys(allData)
                .map(k => storage.fromFirebaseKey(`${nestedPath}/${k}`));
              return keys;
            }
          }
          return [];
        } catch (fbError) {
          console.error('Firebase list error:', fbError);
          throw fbError;
        }
      }
      
      // Try window.storage (Claude artifact environment)
      if (window.storage && window.storage.list) {
        const isShared = shared !== null ? shared : (prefix.startsWith('conflict:') || prefix === 'all-users');
        const result = await window.storage.list(prefix, isShared);
        if (Array.isArray(result)) return result;
        return result?.keys || [];
      }
      
      // Fallback to localStorage
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keys.push(key);
        }
      }
      return keys;
    } catch (error) {
      console.error('Storage list error:', error);
      throw error;
    }
  },
  
  delete: async (key, shared = null) => {
    try {
      if (key.startsWith('conflict:')) return await conflictClient.remove(key.slice(9));
      const resource = accountResourceTarget(key);
      if (resource) { await accessAccountResource({ ...resource, action: 'write', value: null }); return true; }
      // Sanitize key for Firebase
      // Session keys use a per-device path so users don't overwrite each other
      const firebaseKey = storage.isSessionKey(key)
        ? storage.toSessionFirebaseKey(key)
        : storage.toFirebaseKey(key);
      
      // Try Firebase first
      const fbReady = await initFirebase();
      if (fbReady && window.firebaseDb) {
        try {
          await window.firebaseDb.ref(`data/${firebaseKey}`).remove();
          return true;
        } catch (fbError) {
          console.error('Firebase delete error:', fbError);
          throw fbError;
          // Fall through to other methods
        }
      }
      
      // Try window.storage (Claude artifact environment)
      if (window.storage && window.storage.delete) {
        const isShared = shared !== null ? shared : (key.startsWith('conflict:') || key === 'all-users');
        await window.storage.delete(key, isShared);
        return true;
      }
      
      // Fallback to localStorage
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Storage delete error:', error);
      throw error;
    }
  },
  
  // Check which storage backend is active
  getBackendInfo: async () => {
    const fbReady = await initFirebase();
    if (fbReady && window.firebaseDb) {
      return { backend: 'firebase', available: true };
    }
    if (window.storage) {
      return { backend: 'claude', available: true };
    }
    return { backend: 'localStorage', available: true };
  },
  
  // Migration: Copy data from localStorage/Claude to Firebase
};

// Main App Component


// ─── S6: Auth Claims Helper ──────────────────────────────────────────────────
// Calls the setUserClaims Cloud Function to stamp tenantId + role onto the
// Firebase Auth token. Called after every login (all 3 paths).
// After claims are set, forces a token refresh so rules take effect immediately.

async function setAuthClaims(firebaseUid) {
  try {
    const authUser = window.firebaseAuth?.currentUser;
    if (!authUser || authUser.isAnonymous || authUser.uid !== firebaseUid) {
      console.warn('[setAuthClaims] No Firebase Auth user — skipping claims');
      return false;
    }
    // Get current ID token to authenticate the Cloud Function call
    const idToken = await authUser.getIdToken(false);
    const FUNCTIONS_BASE = `https://us-central1-${FIREBASE_CONFIG.projectId}.cloudfunctions.net`;
    const response = await fetch(`${FUNCTIONS_BASE}/setUserClaims`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ uid: firebaseUid }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      console.error('[setAuthClaims] Function error:', err);
      return false;
    }
    const result = await response.json();
    if (!result.identity?.id || result.identity.authUid !== firebaseUid
        || window.firebaseAuth.currentUser !== authUser) return false;
    // Force token refresh so new claims are included in next RTDB request
    await authUser.getIdToken(true);
    const { claims } = await authUser.getIdTokenResult(false);
    if (window.firebaseAuth.currentUser !== authUser || claims.userId !== result.identity.id
        || claims.role !== result.identity.role || claims.tenantId !== result.identity.tenantId) return false;
    // Firebase RTDB WebSocket holds the old token — go offline/online to force re-auth
    if (window.firebaseDb) {
      window.firebaseDb.goOffline();
      await new Promise(r => setTimeout(r, 300));
      window.firebaseDb.goOnline();
      await new Promise(r => setTimeout(r, 300));
    }
    return result.identity;
  } catch (e) {
    console.error('[setAuthClaims] Error:', e);
    return false;
  }
}

// ─── S1: Multi-tenancy — Tenant data model & helpers ────────────────────────

const TENANTS_KEY = 'tenants';
const getTenantUsersKey = (tenantId) => `tenant:${tenantId}:users`;
const getTenantRelsKey  = (tenantId) => `tenant:${tenantId}:relationships`;

const DEFAULT_TENANT = {
  id: 'tenant-hci',
  name: 'Harmony Consulting & Innovations',
  plan: 'enterprise',
  maxSeats: 999,
  activeSeats: 0,
  status: 'active',
  trialExpiresAt: null,
  createdAt: '2026-03-05T00:00:00.000Z',
  createdBy: null,
  settings: {
    requireMentor: false,
    requireNDA: true,
    allowSelfServiceSignup: true,
    brandingName: 'Harmony Consulting & Innovations',
    brandingLogo: null,
  },
  billing: { contactEmail: 'andrea@4hci.com', contactName: 'Andrea Hanes' },
};

const PUBLIC_TENANT = {
  id: 'tenant-public',
  name: 'The ArguMentor',
  plan: 'free',
  maxSeats: 99999,
  activeSeats: 0,
  status: 'active',
  trialExpiresAt: null,
  createdAt: '2026-03-05T00:00:00.000Z',
  createdBy: null,
  settings: {
    requireMentor: false,
    requireNDA: false,
    allowSelfServiceSignup: true,
    brandingName: 'The ArguMentor',
    brandingLogo: null,
  },
  billing: { contactEmail: '', contactName: '' },
};

function planMaxSeats(plan) {
  const map = { free: 5, starter: 25, professional: 100, enterprise: 999 };
  return map[plan] ?? 25;
}

function getTenantBillingMethod(tenant) {
  if (tenant?.billing?.method) return tenant.billing.method;
  if (tenant?.stripeSubscriptionId) return 'stripe';
  return 'manual';
}

function createTenant(overrides = {}) {
  return {
    id: overrides.id || `tenant-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
    name: overrides.name || 'New Tenant',
    plan: overrides.plan || 'starter',
    maxSeats: overrides.maxSeats ?? planMaxSeats(overrides.plan || 'starter'),
    activeSeats: 0,
    status: overrides.status || 'active',
    trialExpiresAt: overrides.trialExpiresAt || null,
    createdAt: new Date().toISOString(),
    createdBy: overrides.createdBy || null,
    settings: {
      requireMentor: false,
      requireNDA: true,
      allowSelfServiceSignup: true,
      brandingName: overrides.name || 'New Tenant',
      brandingLogo: null,
      ...(overrides.settings || {}),
    },
    billing: {
      contactEmail: overrides.billing?.contactEmail || '',
      contactName:  overrides.billing?.contactName  || '',
      method:       overrides.billing?.method       || null,
      managedBy:    overrides.billing?.managedBy     || null,
    },
    cancelledAt: overrides.cancelledAt || null,
    archivedAt: overrides.archivedAt || null,
    legalHold: null,
  };
}

// Reads only the matching entry of the tenants index (orderByChild('id').equalTo).
// Database rules let members query their own organization; only support may read
// the whole index. Without Firebase it falls back to the storage layer.
async function queryTenantIndex(tenantId) {
  if (!tenantId) return [];
  const fbReady = await initFirebase();
  if (!fbReady || !window.firebaseDb) return storage.get(TENANTS_KEY, true);
  const snapshot = await window.firebaseDb
    .ref(`data/${storage.toFirebaseKey(TENANTS_KEY)}`)
    .orderByChild('id').equalTo(tenantId)
    .once('value');
  return snapshot.exists() ? Object.values(snapshot.val()).filter(Boolean) : [];
}

async function loadTenant(tenantId) {
  try {
    const tenants = await queryTenantIndex(tenantId);
    if (!Array.isArray(tenants)) return null;
    return tenants.find(t => t.id === tenantId) || null;
  } catch (e) {
    console.error('loadTenant error:', e);
    return null;
  }
}

async function saveTenant(tenant, allTenants) {
  try {
    const updated = [...(allTenants || []).filter(t => t.id !== tenant.id), tenant];
    await storage.set(TENANTS_KEY, updated, true);
    return updated;
  } catch (e) {
    console.error('saveTenant error:', e);
    return allTenants;
  }
}

async function loadAllTenants() {
  try {
    const tenants = await storage.get(TENANTS_KEY, true);
    if (Array.isArray(tenants) && tenants.length > 0) return tenants;
    // Seed system tenants if empty
    const seed = [DEFAULT_TENANT, PUBLIC_TENANT];
    await storage.set(TENANTS_KEY, seed, true);
    return seed;
  } catch (e) {
    console.error('loadAllTenants error:', e);
    return [DEFAULT_TENANT, PUBLIC_TENANT];
  }
}

// Tenants the signed-in user may read from the index: support gets the full
// list, everyone else only their own organization (from verified claims).
// Callers that loop over organizations use this instead of storage.get('tenants').
async function loadVisibleTenants() {
  const claims = (await window.firebaseAuth?.currentUser?.getIdTokenResult())?.claims;
  if (claims?.role === 'support') return (await storage.get(TENANTS_KEY, true)) || [];
  if (!claims?.tenantId) return [];
  const own = await loadTenant(claims.tenantId);
  return own ? [own] : [];
}

async function resolveTenantId(pendingInvite = null) {
  if (!pendingInvite) return PUBLIC_TENANT.id;
  try {
    if (pendingInvite.conflictId) {
      const conflict = await storage.get(`conflict:${pendingInvite.conflictId}`, true);
      if (conflict?.tenantId) return conflict.tenantId;
    }
    if (pendingInvite.tenantId) return pendingInvite.tenantId;
  } catch (e) {
    console.warn('resolveTenantId fallback to public:', e);
  }
  return PUBLIC_TENANT.id;
}

async function migrateTenantData() {
  // S7: Migration complete — nested paths in place, nothing to migrate
  return { skipped: true };
  try {
    const flag = localStorage.getItem('tenant-migration-v2');
    if (flag) return { skipped: true };
  } catch (e) {}

  const results = { users: 0, relationships: 0, conflicts: 0, tenantsCreated: false };
  try {
    // 1. Seed system tenants
    const existingTenants = await storage.get(TENANTS_KEY, true);
    const seed = Array.isArray(existingTenants) ? existingTenants : [];
    const needsHci    = !seed.find(t => t.id === DEFAULT_TENANT.id);
    const needsPublic = !seed.find(t => t.id === PUBLIC_TENANT.id);
    if (needsHci || needsPublic) {
      const updated = [...seed, ...(needsHci ? [DEFAULT_TENANT] : []), ...(needsPublic ? [PUBLIC_TENANT] : [])];
      await storage.set(TENANTS_KEY, updated, true);
      results.tenantsCreated = true;
    }

    // 2. Migrate users: all-users → tenant:tenant-hci:users
    // Never overwrite existing tenant users — only migrate if target is empty
    const allUsers = await storage.get('all-users', true);
    const existingTenantUsers = await storage.get(getTenantUsersKey(DEFAULT_TENANT.id), true) || [];
    if (Array.isArray(allUsers) && allUsers.length > 0 && existingTenantUsers.length === 0) {
      const stamped = allUsers.map(u => ({ ...u, tenantId: u.tenantId || DEFAULT_TENANT.id }));
      await storage.set(getTenantUsersKey(DEFAULT_TENANT.id), stamped, true);
      results.users = stamped.length;
    }

    // 3. Migrate relationships
    const allRels = await storage.get('relationships', true) || await storage.get('mentor-connections', true);
    if (Array.isArray(allRels) && allRels.length > 0) {
      const stamped = allRels.map(r => ({ ...r, tenantId: r.tenantId || DEFAULT_TENANT.id }));
      await storage.set(getTenantRelsKey(DEFAULT_TENANT.id), stamped, true);
      results.relationships = stamped.length;
    }

    // 4. Stamp tenantId on all conflicts
    try {
      const keys = await storage.list('conflict:', true);
      for (const key of (keys || [])) {
        try {
          const c = await storage.get(key, true);
          if (c && !c.tenantId) {
            await storage.set(key, { ...c, tenantId: DEFAULT_TENANT.id }, true);
            results.conflicts++;
          }
        } catch (e) {}
      }
    } catch (e) { console.warn('conflict stamp error:', e); }

    try { localStorage.setItem('tenant-migration-v1', '1'); localStorage.setItem('tenant-migration-v2', '1'); } catch (e) {}
  } catch (e) {
    console.error('migrateTenantData error:', e);
  }
  return results;
}

// ─── End S1 ──────────────────────────────────────────────────────────────────


// ═══════════════════════════════════════════════════════════════════════
// ROLE DISPLAY LABELS — Single source of truth for user-facing role names
// ═══════════════════════════════════════════════════════════════════════

const ROLE_DISPLAY_LABELS = {
  mentee: 'Participant',
  participant: 'Participant',
  mentor: 'Mentor',
  'fly-on-wall': 'Observer',
  'fly-on-the-wall': 'Observer',
  omniscient: 'Global',
  admin: 'Admin',
  support: 'Support',
};

function getRoleLabel(role) {
  return ROLE_DISPLAY_LABELS[role] || role;
}

// ═══════════════════════════════════════════════════════════════════════
// EMAIL NOTIFICATION CATEGORIES
// Single source of truth for the opt-out categories. The user record
// stores one boolean per key; absent or true means the user is opted in.
// The same keys are used by the scheduled Cloud Functions that send the
// mail and by the unsubscribe link in the email footer, so this list and
// the function-side list must not drift.
// ═══════════════════════════════════════════════════════════════════════
const EMAIL_NOTIF_CATEGORIES = [
  { key: 'emailNotifMood', label: 'Mood check-in reminders' },
  { key: 'emailNotifConflict', label: 'Conflict activity alerts' },
  { key: 'emailNotifDeadline', label: 'Deadline warnings' },
];

// ═══════════════════════════════════════════════════════════════════════
// ROLE HIERARCHY — Level mapping for self-service role promotion
// ═══════════════════════════════════════════════════════════════════════
const ROLE_HIERARCHY = { mentee: 0, participant: 0, mentor: 1, 'fly-on-the-wall': 2, 'fly-on-wall': 2, omniscient: 3, admin: 4, support: 5 };

/**
 * Auto-promote the current user's role when accepting a relationship that
 * requires them to be a higher role than they currently hold.
 * @param {object} rel — the accepted relationship record
 * @param {object} currentUser — the current user (always the receiver)
 * @param {Function} toastFn — toast function for user feedback
 * @returns {object|null} — updated user object if promoted, null otherwise
 */
async function autoPromoteOnAccept(rel, currentUser, toastFn) {
  const expectedRole = rel.receiverRole;
  const expectedLevel = ROLE_HIERARCHY[expectedRole] ?? -1;
  const actualLevel = ROLE_HIERARCHY[currentUser.role] ?? 0;

  if (expectedLevel <= actualLevel) return null; // No promotion needed

  // Check plan: promoted user must have personal+/professional/enterprise
  let billing = null;
  try { billing = await storage.get(`stripe-user:${currentUser.id}`, true); } catch (e) { /* no billing */ }

  // Check org plan via tenant
  let tenantPlan = null;
  if (currentUser.tenantId && currentUser.tenantId !== 'tenant-public') {
    try {
      const myTenant = await loadTenant(currentUser.tenantId);
      if (myTenant?.plan) tenantPlan = myTenant.plan;
    } catch (e) { /* no tenant */ }
  }

  const effectivePlan = tenantPlan || billing?.plan;
  if (!effectivePlan) {
    toastFn(`To serve as ${getRoleLabel(expectedRole)}, you need a Personal+ or organizational plan.`);
    return null;
  }
  const limits = getPlanLimits(effectivePlan);
  if (!limits?.mentorAccess) {
    toastFn(`Your current plan does not support the ${getRoleLabel(expectedRole)} role. Upgrade to Personal+ or an organizational plan.`);
    return null;
  }

  const tenantId = currentUser.tenantId || 'tenant-public';
  const allUsers = await storage.get(getTenantUsersKey(tenantId), true) || [];
  const verifiedUser = allUsers.find(u => u.id === currentUser.id);
  if (verifiedUser?.role !== expectedRole && !(verifiedUser?.role === 'fly-on-the-wall' && expectedRole === 'fly-on-wall')) return null;

  // Update Firebase Auth claims
  if (currentUser.authUid) {
    await setAuthClaims(currentUser.authUid, tenantId, expectedRole);
  }

  toastFn(`Your role has been upgraded to ${getRoleLabel(expectedRole)}! Please refresh the page to access your new features.`);
  return { ...currentUser, role: expectedRole };
}

// ═══════════════════════════════════════════════════════════════════════
// PLAN ENFORCEMENT — Re-exported from billing module for backward compatibility
// Actual logic lives in src/modules/billing/BillingEnforcement.js
// ═══════════════════════════════════════════════════════════════════════
import { PLAN_LIMITS, getPlanLimits } from './modules/billing/BillingConfig.js';
import { canCreateConflict, canAccessMentor, canAccessNonParticipantRole, checkSeatCapacity, getTrialStatus, getTenantDaysRemaining, isTenantReadOnly, isBillingReadOnly } from './modules/billing/BillingEnforcement.js';
// ─── End Plan Enforcement ────────────────────────────────────────────────────


export { ToastContext, ToastProvider, useToast, CrisisContext, useCrisisAlert, calculateWorkingHours, isWithinResponseHours, addAuditEntry, FIREBASE_CONFIG, OWL_LOGO, initFirebase, registerServiceWorker, notificationPermissionGranted, requestNotificationPermission, sendNotification, sendPushToUser, initFCM, removeFCMToken, EMAIL_CONFIG, emailService, getUrlParams, clearUrlParams, useVoiceInput, VoiceInputButton, detectHarmContent, detectUnconstructiveContent, detectConnotationIssues, containsProfanity, notifyMentorOfHarmDetection, CrisisResourceModal, storage, FCM_VAPID_KEY, logAnalyticsEvent, setAnalyticsUser, RELATIONSHIPS_KEY, migrateRelationships, loadRelationships, loadAllRelationships, saveRelationship, deriveConflictAccess, computeRelationalAccessSnapshot, getVisibleConflicts, DEFAULT_TENANT, PUBLIC_TENANT, TENANTS_KEY, getTenantUsersKey, getTenantRelsKey, createTenant, planMaxSeats, getTenantBillingMethod, loadTenant, saveTenant, loadAllTenants, loadVisibleTenants, migrateTenantData, resolveTenantId, setAuthClaims, logHarmDetectionEvent, RelationshipsContext, RelationshipsProvider, useRelationships, PLAN_LIMITS, getPlanLimits, canCreateConflict, canAccessMentor, canAccessNonParticipantRole, checkSeatCapacity, getTrialStatus, getTenantDaysRemaining, isTenantReadOnly, isBillingReadOnly, STRIPE_CONFIG, ROLE_DISPLAY_LABELS, getRoleLabel, ROLE_HIERARCHY, EMAIL_NOTIF_CATEGORIES, autoPromoteOnAccept, directoryClient };
